import { ReactNode, useContext, useEffect, useMemo, useState, type ContextType } from 'react';
import { Outlet, useMatch, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { StatusLabel } from '@shared/components/StatusLabel';
import { DocumentBulletListRegular } from '@fluentui/react-icons';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import type { TMap } from '@shared/types';
import { ChangeNoteDialog } from '@shared/components/dialogs/ChangeNoteDialog';
import { NAV_DRAWER_COLLAPSED_WIDTH_PX } from '@shared/components/nav/NavDrawer';
import { Path } from '@shared/components/Path';
import { layoutTransition } from '@shared/constants/layoutTransition.constants';
import { ToastContext } from '@shared/contexts/ToastContext.context';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks/useFolderPathAsBreadcrumbs.hook';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import SignInContext from '@modules/users/contexts/SignInContext.context';
import { bumpVersionComponent } from '@modules/items';
import { AccessType } from '@modules/access/types';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import { AUTO_EDIT_QUERY_PARAM, ITEMS_NAV_DRAWER_WIDTH_PX } from '@modules/items/constants';
import ItemsNavDrawer from '@modules/items/components/nav/ItemsNavDrawer';
import type { IFolder, TPath } from '@modules/items/types/folders.types';
import {
  DocumentContext,
  DocumentProvider,
  ShareDocumentSession,
  useDocumentNavTabs,
  useEmbeddedEditMode,
} from '@modules/documents';
import { COMPLETE_WHILE_CONNECTING_FAILED_MESSAGE } from '@modules/documents/constants';
import { useChangeTemplateDocument, useCompleteChangeTemplate } from '../api';
import { CHANGE_TEMPLATE_DETAILS_MAX_WIDTH_PX } from '../constants';
import { CHANGE_TEMPLATES_PATH, getChangeTemplatePath } from '../paths';
import { useChangeTemplateView } from '../hooks/useChangeTemplateView.hook';
import {
  EChangeTemplateSection,
  isTChangeTemplateVersion,
  type TChangeTemplateDetails,
  type TChangeTemplateOutletContext,
} from '../types';
import ChangeTemplateNavOverview from './ChangeTemplateNavOverview';
import ChangeTemplateSectionTabs from './ChangeTemplateSectionTabs';

type TProps = {
  qmFolder: IFolder;
  rootFolder: IFolder;
  details: TChangeTemplateDetails;
};

type TNavDrawerProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  resolvedPath: TPath;
  overview: ReactNode;
  isDocumentLoading: boolean;
};

// Inside the shared session's provider, so the shown document's tabs see it.
const ChangeTemplateNavDrawer = ({ isOpen, setIsOpen, resolvedPath, overview, isDocumentLoading }: TNavDrawerProps) => {
  const { Insights } = useDocumentNavTabs();
  const tabs: TMap<string, ReactNode> = { Overview: overview };
  // Kept while the next section's document loads, so a selected Insights tab stays selected.
  if (Insights || isDocumentLoading) tabs.Insights = Insights ?? <StatusLabel data={null} />;
  return (
    <ItemsNavDrawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      hrefBack={CHANGE_TEMPLATES_PATH}
      resolvedPath={resolvedPath}
      tabs={tabs}
    />
  );
};

const ChangeTemplateContent = ({ qmFolder, rootFolder, details }: TProps) => {
  const { changeTemplate, permissions } = details;
  const { canEdit, editLockedReason } = useCanEdit(permissions.accessRight);
  const { selected, templateVersion } = useChangeTemplateView(changeTemplate, permissions.accessRight);
  const hasDraft = !!changeTemplate.draft;
  // Only the known sections have child routes, and General is the index route.
  const section =
    (useMatch('/change-template/:id/:section/*')?.params.section as EChangeTemplateSection | undefined) ??
    EChangeTemplateSection.GENERAL;
  // The parent route's params include the open assessment's.
  const { assessmentId } = useParams<{ assessmentId?: string }>();
  // An assessment missing from the selected version has no document to show.
  const documentId =
    section === EChangeTemplateSection.REQUEST
      ? selected.request.document
      : selected.assessments.find((assessment) => assessment._id === assessmentId)?.document;
  const { data: document, error: documentError } = useChangeTemplateDocument(
    changeTemplate._id,
    documentId,
    templateVersion
  );
  const { autoEdit, isEditMode } = useEmbeddedEditMode();
  const { changeMany } = useSearchParams();
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const drawerWidth = isDrawerOpen ? ITEMS_NAV_DRAWER_WIDTH_PX : NAV_DRAWER_COLLAPSED_WIDTH_PX;
  const [session, setSession] = useState<ContextType<typeof DocumentContext> | null>(null);
  // Edit mode starts editing the section's document once it has loaded, so until then it counts as connecting.
  const isEditorPending = Boolean(documentId) && autoEdit && !session && !documentError;
  const { organization } = useContext(SignInContext);
  const { show } = useContext(ToastContext);
  const complete = useCompleteChangeTemplate(changeTemplate._id);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const nextVersionId = changeTemplate.versions[0]?.id ? bumpVersionComponent(changeTemplate.versions[0].id, 1) : '0.1';
  const editDisabledReason = templateVersion
    ? `${hasDraft ? 'View draft' : 'View latest and create a draft'} to add assessments.`
    : '';
  // Like its documents, the template changes only in a draft and in edit mode.
  const assessmentsDisabledReason =
    editDisabledReason ||
    (isTChangeTemplateVersion(selected) ? 'Create a draft to add assessments.' : '') ||
    (isEditMode ? '' : `Edit draft to edit this ${TERMINOLOGY.changeTemplate.lower}.`);
  // The title, description and assessments change under the same rule.
  const canChange = canEdit && !assessmentsDisabledReason;
  // The editor's auto-edit checks no permission, so drop a copied edit link where editing cannot start.
  const canStartEditing = canEdit && !editDisabledReason && hasDraft;
  // Ends the edit view too, so this write cannot bring back a `view=edit` that the editor has just cleared.
  useEffect(() => {
    if (autoEdit && !canStartEditing) changeMany({ view: undefined, [AUTO_EDIT_QUERY_PARAM]: undefined });
  }, [autoEdit, canStartEditing]);
  const openCompleteDialog = () => {
    session?.prepareComplete();
    setIsCompleteDialogOpen(true);
  };
  // Like a Document's Complete, under the shown document's editing lock.
  const completeVersion = async (note: string) => {
    if (isEditorPending) {
      show('error', COMPLETE_WHILE_CONNECTING_FAILED_MESSAGE);
      throw new Error('Completing while the editor connects.');
    }
    return session
      ? session.completeWhileEditing((editingDocumentId) => complete.mutateAsync({ note, editingDocumentId }))
      : complete.mutateAsync({ note });
  };
  const fullPath: TPath = [
    { _id: qmFolder._id, name: qmFolder.name, accessRight: qmFolder.accessRight },
    {
      _id: rootFolder._id,
      name: TERMINOLOGY.changeTemplate.upperPlural,
      accessRight: rootFolder.accessRight,
      href: CHANGE_TEMPLATES_PATH,
    },
    {
      _id: changeTemplate._id,
      name: changeTemplate.title,
      icon: DocumentBulletListRegular,
      accessRight: AccessType.NONE,
      href: getChangeTemplatePath(changeTemplate._id),
    },
  ];
  const outletContext = useMemo<TChangeTemplateOutletContext>(
    () => ({
      details,
      selected,
      canEdit,
      editLockedReason,
      canChange,
      assessmentsDisabledReason,
      documentError,
      document,
      templateVersion,
    }),
    [
      details,
      selected,
      canEdit,
      editLockedReason,
      canChange,
      assessmentsDisabledReason,
      documentError,
      document,
      templateVersion,
    ]
  );
  // The same element on every render, so a change of the shared session does not render its provider again.
  const documentSection = useMemo(
    () => (
      <>
        <ShareDocumentSession onChange={setSession} />
        <Outlet context={outletContext} />
      </>
    ),
    [outletContext]
  );
  // The rail and drawer read the shown document's session here (null without one), so they outlive its changes.
  return (
    <DocumentContext.Provider value={session as ContextType<typeof DocumentContext>}>
      {/* Right padding as wide as the drawer centers the content on the window, and shrinks before the content. */}
      <div
        className="flex flex-1 min-h-0"
        style={{
          paddingRight: `clamp(0px, calc(100vw - ${drawerWidth}px - ${CHANGE_TEMPLATE_DETAILS_MAX_WIDTH_PX}px), ${drawerWidth}px)`,
          transition: layoutTransition('padding-right'),
        }}
      >
        <ChangeTemplateNavDrawer
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          resolvedPath={fullPath}
          isDocumentLoading={Boolean(documentId) && !session && !documentError}
          overview={
            <ChangeTemplateNavOverview
              details={details}
              selected={selected}
              canEdit={canEdit}
              editLockedReason={editLockedReason}
              isEditorPending={isEditorPending}
              onComplete={openCompleteDialog}
              templateVersion={templateVersion}
            />
          }
        />
        {/* A document section fills the height, so its paper scrolls in its own box. */}
        {/* The Document page's padding, which keeps a landscape page inside the width. */}
        <div
          className={classNames('mx-auto flex min-h-0 min-w-0 flex-1 flex-col gap-5 pt-4 pl-3 pr-4', {
            'pb-16': !documentId,
          })}
          style={{ maxWidth: CHANGE_TEMPLATE_DETAILS_MAX_WIDTH_PX }}
        >
          <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
          <ChangeTemplateSectionTabs
            changeTemplateId={changeTemplate._id}
            section={section}
            isEditMode={isEditMode}
            assessmentId={assessmentId}
            templateVersion={templateVersion}
          />
          {document ? (
            <DocumentProvider
              // Another document or template version mounts a fresh session.
              key={`${documentId}:${templateVersion ?? ''}`}
              doc={document.doc}
              permissions={document.permissions}
              resolvedItems={document.resolvedItems}
              resolvedItemNames={document.resolvedItemNames}
            >
              {documentSection}
            </DocumentProvider>
          ) : (
            <Outlet context={outletContext} />
          )}
        </div>
      </div>
      {/* Outside the drawer, whose tab switch would unmount it with the typed note. */}
      <ChangeNoteDialog
        isOpen={isCompleteDialogOpen}
        setIsOpen={setIsCompleteDialogOpen}
        title={`Complete version ${nextVersionId}`}
        onComplete={completeVersion}
        defaultNote={organization.versioningConfig?.changeNoteDefault}
      />
    </DocumentContext.Provider>
  );
};

export default ChangeTemplateContent;
